import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ScrollBarContainer, Toggle } from '@playbookux/playbook-web-core';
import {
  arrayOf, bool, func, number, objectOf, oneOf, shape, string,
} from 'prop-types';
import Transcript from './transcript';
import AnswerTranscript from './answerTranscript';

import { DELETE_TAG_HIGHLIGHT } from '../graphql';
import {
  EDIT_TEST_SESSION_SPEAKER,
  EDIT_ANSWER_TRANSCRIPT,
  updateAnswersTagHighlightsDeleted,
  updateSpeakersTagHighlightsDeleted,
} from '../../../graphql/test-sessions';

import {
  ContainerTranscripts,
  Loader,
  ContainerToggle,
  TextToggle,
  ButtonResumeAutoscroll,
} from './styles';
import { PROJECT_TYPES_ENUMS } from '../../../constants/projects';

const Transcripts = (props) => {
  const {
    testSessionId,
    testType,
    surveys,
    onCurrentTime,
    testSession,
    loading,
    scrollBar,
    autoScrollTranscript,
    stopScrolling,
    currentTime,
  } = props;

  // show button
  const [showButton, setShowButton] = useState(false);
  const [toggleAutoScroll, setToggleAutoScroll] = useState(true);

  const [editTranscript] = useMutation(EDIT_TEST_SESSION_SPEAKER);
  const [editAnswerTranscript] = useMutation(EDIT_ANSWER_TRANSCRIPT);
  const [deleteTagHighlight] = useMutation(DELETE_TAG_HIGHLIGHT);

  const handleScroll = () => {
    if (stopScrolling.current) {
      stopScrolling.current = false;
    } else {
      setShowButton(true);
      autoScrollTranscript.current = false;
    }
  };

  const onClickResumeAutoScroll = async () => {
    setShowButton(false);
    autoScrollTranscript.current = true;
    const questions = document.querySelectorAll('[data-transcript-video-start-time]');
    let targetElement = null;
    let smallestDifference = Infinity;
    // Why do we have 2 different loops?
    // the unmod it is 100% fine for the method, but for mod below, it is not because of small difference of seconds.
    // so the method below is to find the closest speech to the current time.
    if (testType === 'moderated') {
      questions.forEach((question) => {
        const start = parseFloat(question.getAttribute('data-transcript-video-start-time'));

        const difference = Math.abs(currentTime - start);

        if (difference < smallestDifference) {
          smallestDifference = difference;
          targetElement = question;
        }
      });
    } else {
      // since we have markers, it is easier to find the closest speech to the current time.
      questions.forEach((question) => {
        const start = parseFloat(question.getAttribute('data-transcript-video-start-time'));
        const end = parseFloat(question.getAttribute('data-transcript-video-end-time'));

        if (currentTime >= start && currentTime <= end) {
          targetElement = question;
        }
      });
    }

    if (targetElement && scrollBar.current) {
      const { offsetTop } = targetElement;
      stopScrolling.current = true;
      scrollBar.current.scrollTop(offsetTop);
    }
  };

  const handleChangeAnswerTranscript = (value, id) => {
    editAnswerTranscript({ variables: { testSessionAnswerId: id, transcript: value } });
  };

  const handleChangeTranscript = (id, text) => {
    editTranscript({
      variables: {
        id,
        attributes: {
          text,
        },
      },
    });
  };

  const filteredTranscript = useMemo(() => testSession?.testSessionSpeakers, [testSession]);

  const handleRemoveTagHighlightFromAnswer = (answerId, tagHighlight) => {
    deleteTagHighlight({
      variables: {
        ids: [tagHighlight.id],
      },
      update: updateAnswersTagHighlightsDeleted(testSessionId, answerId, tagHighlight),
    });
  };

  const handleRemoveTagHighlightFromSpeaker = (speakerId, tagHighlight) => {
    deleteTagHighlight({
      variables: {
        ids: [tagHighlight.id],
      },
      update: updateSpeakersTagHighlightsDeleted(testSessionId, speakerId, tagHighlight),
    });
  };

  const onChangeToggle = async () => {
    autoScrollTranscript.current = !toggleAutoScroll;
    setToggleAutoScroll(!toggleAutoScroll);
    setShowButton(false);
    if (autoScrollTranscript.current) {
      onClickResumeAutoScroll();
    }
  };

  if (testType === PROJECT_TYPES_ENUMS.MODERATED || testType === PROJECT_TYPES_ENUMS.UPLOAD) {
    return (
      <ContainerTranscripts id="full-transcript">
        <ScrollBarContainer
          onScroll={handleScroll}
          refScroll={scrollBar}
        >
          <ContainerToggle>
            <Toggle
              onChange={onChangeToggle}
              checked={toggleAutoScroll}
              id="toggle-autoscroll-moderated"
            />
            <TextToggle>
              Autoscroll the transcript
            </TextToggle>
          </ContainerToggle>

          {loading && (
            <Loader data-loader="transcripts-mod-loader" />
          )}
          {!loading && filteredTranscript.map((speaker) => (
            <Transcript
              key={speaker.id}
              id={speaker.id}
              speakerTag={speaker.speakerTag}
              startTime={speaker.videoStartTime}
              endTime={speaker.videoEndTime}
              onCurrentTime={onCurrentTime}
              transcript={speaker.text}
              tagHighlights={speaker.tagHighlights}
              onSubmit={(value) => handleChangeTranscript(speaker.id, value)}
              onRemoveTagHighlight={handleRemoveTagHighlightFromSpeaker}
              transcriptHighlights={speaker.transcriptHighlights}
            />
          ))}
        </ScrollBarContainer>
        {
          showButton && toggleAutoScroll && (
            <ButtonResumeAutoscroll
              onClick={onClickResumeAutoScroll}
            >
              Resume autoscrolling
            </ButtonResumeAutoscroll>
          )
        }
      </ContainerTranscripts>
    );
  }

  return (
    <ContainerTranscripts id="full-transcript">
      <ScrollBarContainer
        onScroll={handleScroll}
        refScroll={scrollBar}
      >
        <ContainerToggle>
          <Toggle
            onChange={onChangeToggle}
            checked={toggleAutoScroll}
            id="toggle-autoscroll-unmoderated"
          />
          <TextToggle>
            Autoscroll the transcript
          </TextToggle>
        </ContainerToggle>
        {loading && (
          <Loader data-loader="transcripts-loader" />
        )}
        {!loading && (
          <AnswerTranscript
            surveys={surveys}
            onCurrentTime={onCurrentTime}
            testSession={testSession}
            onSubmit={(value, id) => handleChangeAnswerTranscript(value, id)}
            onRemoveTagHighlight={handleRemoveTagHighlightFromAnswer}
          />
        )}
      </ScrollBarContainer>
      {
        showButton && toggleAutoScroll && (
          <ButtonResumeAutoscroll
            onClick={onClickResumeAutoScroll}
          >
            Resume autoscrolling
          </ButtonResumeAutoscroll>
        )
      }
    </ContainerTranscripts>
  );
};

Transcripts.defaultProps = {
  testSessionId: '',
};

Transcripts.propTypes = {
  testType: oneOf(['moderated', 'unmoderated']).isRequired,
  testSessionId: string,
  testSession: shape(),
  surveys: arrayOf(shape()).isRequired,
  onCurrentTime: func.isRequired,
  loading: bool,
  scrollBar: objectOf(bool).isRequired,
  autoScrollTranscript: objectOf(bool).isRequired,
  stopScrolling: objectOf(bool).isRequired,
  currentTime: number.isRequired,
};

Transcripts.defaultProps = {
  testSession: null,
  loading: false,
};

export default Transcripts;
